import { useEffect, useRef, useState } from "react";
import {
  X, ChevronLeft, ChevronRight, Check, Loader2, Briefcase, Palette,
  ScrollText, Upload, Link2, Sparkles, Trash2, Save, Sliders,
  Phone, MessageCircleMore, ExternalLink, CreditCard, Landmark, Smartphone, Globe,
  Wand2,
} from "lucide-react";
import { useGender } from "../lib/gender";
import { toast } from "sonner";
import { api } from "../lib/api";
import confetti from "canvas-confetti";
import {
  FONT_OPTIONS, BUTTON_STYLES, FEATURES, SOCIALS,
} from "../lib/briefConfig";
import { BriefAutomationVisualizer } from "./BriefAutomationVisualizer";

/**
 * Colourful, multi-step client onboarding questionnaire ("אפיון לקוח").
 *
 * Steps:
 *  1) מה העסק עושה
 *  2) צבעים + פונט + סוג כפתורים
 *  3) פירוט (קהל יעד, שירותים)
 *  4) פיצ'רים ורשתות חברתיות
 *  5) קבצים
 *  6) אתרי השראה
 *  ▶ Post-finish: דף רכישת קרדיטים
 *
 * Right side of the modal renders a LIVE MOCK WEBSITE that reacts to
 * every field the client fills in, so they see their business being
 * built in real-time.
 */

const STEPS = [
  { key: "what", label: "העסק שלך", icon: Briefcase, hue: "from-amber-300 via-orange-400 to-rose-400" },
  { key: "colors", label: "עיצוב", icon: Palette, hue: "from-pink-400 via-fuchsia-400 to-violet-400" },
  { key: "details", label: "פירוט", icon: ScrollText, hue: "from-emerald-300 via-teal-400 to-cyan-400" },
  { key: "features", label: "פיצ'רים", icon: Sliders, hue: "from-cyan-400 via-sky-500 to-indigo-500" },
  { key: "files", label: "קבצים", icon: Upload, hue: "from-blue-400 via-indigo-500 to-purple-500" },
  { key: "refs", label: "השראה", icon: Link2, hue: "from-yellow-300 via-lime-400 to-emerald-400" },
  { key: "automations", label: "האוטומציות שלך 🚀", icon: Wand2, hue: "from-rose-500 via-fuchsia-500 to-violet-600" },
];

const REP_PHONE = "0504944192";
const REP_WHATSAPP = `https://wa.me/972504944192`;
const EXAMPLES_LINK = "https://flowup.co.il/examples";  // placeholder

export function ClientBriefWizard({ companyId, open, onClose, onSaved, company, onOpenTopup }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  // Thank-you modal that pops between brief-finish and pricing
  const [showThanks, setShowThanks] = useState(false);
  // "What is a credit?" modal that pops between pricing and top-up
  const [showCreditInfo, setShowCreditInfo] = useState(false);
  // "Important info" popup shown ONCE at the very start of a fresh brief
  const [showIntro, setShowIntro] = useState(false);

  const [data, setData] = useState({
    business_what: "",
    business_industry: "",
    primary_color: "#7c3aed",
    secondary_color: "#ec4899",
    accent_color: "#fde047",
    font_family: "heebo",
    button_style: "rounded",
    business_about: "",
    target_audience: "",
    tone_of_voice: "",
    key_services: "",
    features: [],
    facebook_url: "",
    instagram_url: "",
    tiktok_url: "",
    files: [],
    reference_urls: [{ url: "", reason: "" }, { url: "", reason: "" }, { url: "", reason: "" }, { url: "", reason: "" }, { url: "", reason: "" }],
    automation_needs: "",
    automation_pains: [],
    gender: "",  // hydrated from global hook below
    finished: false,
  });

  // Global gender preference — set on homepage CTA click, used everywhere.
  const [globalGender] = useGender();
  // Hydrate brief.gender from the global hook so any later "t()" call works
  // even before the brief is fetched from the server.
  useEffect(() => {
    if (globalGender) setData((d) => (d.gender ? d : { ...d, gender: globalGender }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalGender]);

  useEffect(() => {
    if (!open || !companyId) return;
    (async () => {
      try {
        const { data: r } = await api.get(`/companies/${companyId}/brief`);
        const hasContent = r.brief && Object.keys(r.brief).length > 0 && (
          r.brief.business_what || r.brief.business_industry || r.brief.finished
        );
        if (r.brief && Object.keys(r.brief).length > 0) {
          setData((d) => ({
            ...d,
            ...r.brief,
            features: r.brief.features || [],
            reference_urls: (r.brief.reference_urls && r.brief.reference_urls.length > 0)
              ? [...r.brief.reference_urls, ...Array(Math.max(0, 5 - r.brief.reference_urls.length)).fill({ url: "", reason: "" })]
              : d.reference_urls,
            files: r.brief.files || [],
          }));
        }
        // First-time user: show the "important info" popup before they
        // start. Don't pester returning users whose brief already has
        // content.
        if (!hasContent) {
          try {
            const seen = sessionStorage.getItem(`flowup_brief_intro_${companyId}`);
            if (!seen) setShowIntro(true);
          } catch {
            setShowIntro(true);
          }
        }
      } catch { /* silent — handled elsewhere */ }
    })();
  }, [open, companyId]);

  const dismissIntro = () => {
    setShowIntro(false);
    try { sessionStorage.setItem(`flowup_brief_intro_${companyId}`, "1"); } catch { /* no-op */ }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleFeature = (k) => setData((d) => ({
    ...d, features: d.features.includes(k) ? d.features.filter((x) => x !== k) : [...d.features, k]
  }));

  // Gender-aware Hebrew helper. Pass (male, female, fallback).
  // If the user didn't choose, falls back to male (Hebrew default).
  const t = (m, f, neutral) => {
    if (data.gender === "female") return f;
    if (data.gender === "male") return m;
    return neutral || m;
  };

  const save = async (markFinished = false) => {
    setBusy(true);
    try {
      const cleanedRefs = data.reference_urls.filter((r) => r.url && r.url.trim());
      const payload = { ...data, reference_urls: cleanedRefs, finished: markFinished };
      await api.put(`/companies/${companyId}/brief`, payload);
      toast.success(markFinished ? "האפיון נשמר! 🎉" : "האפיון נשמר", { duration: 3500 });
      onSaved?.();
      if (markFinished) {
        setShowThanks(true);
        // Confetti burst — colourful + repeating
        const fire = (origin, colors) => {
          confetti({ particleCount: 80, spread: 70, startVelocity: 45, origin, colors, scalar: 1.1 });
        };
        const palette = ["#a855f7", "#ec4899", "#f59e0b", "#22c55e", "#06b6d4", "#fbbf24"];
        fire({ x: 0.2, y: 0.7 }, palette);
        fire({ x: 0.8, y: 0.7 }, palette);
        setTimeout(() => fire({ x: 0.5, y: 0.5 }, palette), 250);
        setTimeout(() => fire({ x: 0.3, y: 0.6 }, palette), 600);
        setTimeout(() => fire({ x: 0.7, y: 0.6 }, palette), 900);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשמירה");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const cur = STEPS[step];

  // === Post-finish: thank-you modal → pricing → credit-info → topup ===
  if (showCreditInfo) {
    return (
      <CreditInfoModal
        gender={data.gender}
        onContinue={() => {
          setShowCreditInfo(false);
          onClose?.();
          onOpenTopup?.();
        }}
      />
    );
  }

  if (showThanks && !showPricing) {
    return (
      <ThankYouModal
        onContinue={() => {
          setShowThanks(false);
          setShowPricing(true);
        }}
      />
    );
  }

  if (showPricing) {
    return (
      <CreditsPricingScreen
        company={company}
        onClose={() => { setShowPricing(false); onClose?.(); }}
        onBuyCredits={() => {
          // Pop the "what is a credit?" educational modal FIRST.  Only
          // after the customer presses "המשך 👇" do we open the actual
          // top-up modal.
          setShowPricing(false);
          setShowCreditInfo(true);
        }}
        onBackToBrief={() => { setShowPricing(false); }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[10003] bg-black/70 backdrop-blur-md flex items-center justify-center p-2 sm:p-6" data-testid="brief-wizard">
      {/* Important info popup — shown ONCE before the user starts filling. */}
      {showIntro && <BriefIntroPopup onContinue={dismissIntro} gender={data.gender} />}
      {/* Gender selection lives globally on the homepage now — no inline modal here. */}
      <div className="relative w-full max-w-6xl h-[94vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col" dir="rtl">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: `radial-gradient(circle, ${data.accent_color}, transparent)` }} />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: `radial-gradient(circle, ${data.secondary_color}, transparent)` }} />

        {/* Header */}
        <header className="relative px-5 sm:px-8 py-4 border-b border-stone-100 flex items-center justify-between gap-3 bg-white z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-amber-400 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-lg sm:text-xl font-extrabold text-stone-900 truncate">{t("בוא נבנה את העסק שלך", "בואי נבנה את העסק שלך")}</h2>
              <p className="text-[11px] text-stone-500 truncate">{t("מלא את הפרטים — נחזור אליך עם תכנית עבודה ✨", "מלאי את הפרטים — נחזור אליך עם תכנית עבודה ✨")}</p>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg text-stone-500 hover:bg-stone-100 inline-flex items-center justify-center" data-testid="brief-close">
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Stepper */}
        <div className="px-5 sm:px-8 py-3 border-b border-stone-100 bg-white z-10">
          <div className="flex items-center gap-1 sm:gap-1.5 mb-2 overflow-x-auto pb-1">
            {STEPS.map((s, i) => {
              const Done = i < step;
              const Active = i === step;
              const Sicon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition flex-shrink-0 ${
                    Active ? `bg-gradient-to-l ${s.hue} text-white shadow-md` :
                    Done ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                  data-testid={`brief-step-${s.key}`}
                >
                  {Done ? <Check className="h-3.5 w-3.5" /> : <Sicon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{i + 1}. {s.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
              );
            })}
          </div>
          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-l ${cur.hue} transition-all duration-500`} style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        {/* Body: form on right (RTL) + LIVE MOCK on left */}
        <div className="relative flex-1 overflow-hidden z-10">
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-0 h-full">
            {/* Form */}
            <div className="p-5 sm:p-8 overflow-y-auto">
              {step === 0 && <StepBusiness data={data} set={set} t={t} companyId={companyId} />}
              {step === 1 && <StepDesign data={data} set={set} t={t} />}
              {step === 2 && <StepDetails data={data} set={set} t={t} />}
              {step === 3 && <StepFeatures data={data} set={set} toggleFeature={toggleFeature} t={t} />}
              {step === 4 && <StepFiles data={data} set={set} t={t} />}
              {step === 5 && <StepReferences data={data} set={set} t={t} />}
              {step === 6 && <StepAutomations data={data} set={set} t={t} />}
            </div>

            {/* Friendly illustrated visualizer — blends with the form
                (no dark frame). One illustration per step. */}
            <div className="overflow-hidden border-t md:border-t-0 md:border-r border-stone-100 hidden md:block bg-gradient-to-br from-rose-50/40 via-white to-sky-50/40">
              <BriefAutomationVisualizer data={data} step={step} accent={data.primary_color} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="relative px-5 sm:px-8 py-3 border-t border-stone-100 bg-white flex items-center justify-between gap-3 z-10">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-bold text-stone-600 hover:bg-stone-100 disabled:opacity-30 transition" data-testid="brief-prev">
            <ChevronRight className="h-4 w-4" /> חזור
          </button>
          <button onClick={() => save(false)} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-bold text-stone-700 bg-stone-100 hover:bg-stone-200 disabled:opacity-50 transition" data-testid="brief-save-draft">
            <Save className="h-4 w-4" /> שמור כטיוטה
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              className={`inline-flex items-center gap-1 px-5 py-2.5 rounded-xl text-sm font-extrabold text-white shadow-lg bg-gradient-to-l ${cur.hue} hover:opacity-90 transition`} data-testid="brief-next">
              הבא <ChevronLeft className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => save(true)} disabled={busy}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-extrabold text-white shadow-lg bg-gradient-to-l from-emerald-500 via-teal-500 to-cyan-500 hover:opacity-90 disabled:opacity-50 transition" data-testid="brief-finish">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> סיים ושמור</>}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

// ============== LIVE PREVIEW ==============
function LiveWebsitePreview({ data, company }) {
  const font = FONT_OPTIONS.find((f) => f.key === data.font_family) || FONT_OPTIONS[0];
  const btn = BUTTON_STYLES.find((b) => b.key === data.button_style) || BUTTON_STYLES[0];
  const enabledFeatures = FEATURES.filter((f) => data.features.includes(f.key));
  const heading = company?.display_name || company?.name || "העסק שלך";
  const tagline = data.business_what || "תיאור קצר של העסק שלך יופיע פה ברגע שתמלא את השלב הראשון…";

  const buttonClass = `${btn.radius} font-bold px-4 py-2 text-sm shadow inline-flex items-center gap-1.5 transition`;
  const buttonStyle = btn.key === "outline"
    ? { background: "white", color: data.primary_color, border: `2px solid ${data.primary_color}` }
    : { background: data.primary_color, color: "#fff", border: "none" };

  return (
    <div className="rounded-2xl bg-white shadow-2xl overflow-hidden border-4 border-stone-300" style={{ fontFamily: font.css }} data-testid="live-preview">
      {/* Mock browser chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 border-b border-stone-200">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="flex-1 mx-2 h-5 rounded bg-white border border-stone-200 px-2 text-[9px] text-stone-500 flex items-center" dir="ltr">
          {company?.slug ? `${company.slug}.flowup.co.il` : "yourdomain.co.il"}
        </span>
      </div>

      {/* Site header */}
      <header className="flex items-center justify-between gap-2 px-4 py-3" style={{ background: data.primary_color }}>
        <div className="flex items-center gap-2">
          {company?.logo_url ? (
            <div className="h-9 w-9 rounded-lg bg-white p-0.5 overflow-hidden">
              <img src={company.logo_url} alt="" className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center font-extrabold text-base" style={{ color: data.primary_color }}>
              {heading.charAt(0)}
            </div>
          )}
          <span className="text-white font-extrabold text-sm">{heading}</span>
        </div>
        <button className={buttonClass} style={{ background: data.accent_color, color: "#1c1917" }}>
          התחברות
        </button>
      </header>

      {/* Hero */}
      <section className="px-4 py-6 text-center" style={{ background: `linear-gradient(135deg, ${data.primary_color}10, ${data.secondary_color}10)` }}>
        <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: data.secondary_color }}>
          {data.business_industry || "תחום עיסוק"}
        </div>
        <h1 className="font-extrabold text-2xl leading-tight mb-2" style={{ color: data.primary_color }}>{heading}</h1>
        <p className="text-xs text-stone-600 leading-relaxed line-clamp-3 mb-3">{tagline}</p>
        <button className={buttonClass} style={buttonStyle}>
          {data.tone_of_voice === "יוקרתי" ? "הזמינו ייעוץ" : "התחילו עכשיו"} <ChevronLeft className="h-3 w-3" />
        </button>
      </section>

      {/* Features grid */}
      {enabledFeatures.length > 0 && (
        <section className="px-4 py-4 border-t border-stone-100" data-testid="preview-features">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-2 text-center">השירותים שלנו</div>
          <div className="grid grid-cols-3 gap-2">
            {enabledFeatures.slice(0, 6).map((f) => (
              <div key={f.key} className="rounded-lg p-2 text-center border" style={{ borderColor: data.primary_color + "33", background: data.primary_color + "08" }}>
                <div className="text-xl">{f.emoji}</div>
                <div className="text-[9px] font-bold leading-tight mt-1" style={{ color: data.primary_color }}>{f.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Services text */}
      {data.key_services && (
        <section className="px-4 py-3 border-t border-stone-100">
          <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: data.secondary_color }}>שירותים</div>
          <p className="text-[11px] text-stone-700 whitespace-pre-wrap line-clamp-4">{data.key_services}</p>
        </section>
      )}

      {/* Socials footer */}
      {(data.facebook_url || data.instagram_url || data.tiktok_url) && (
        <footer className="px-4 py-3 border-t border-stone-100 flex items-center justify-center gap-3 text-xs" style={{ background: data.primary_color + "08" }}>
          {data.facebook_url && <a className="font-bold" style={{ color: data.primary_color }}>Facebook</a>}
          {data.instagram_url && <a className="font-bold" style={{ color: data.primary_color }}>Instagram</a>}
          {data.tiktok_url && <a className="font-bold" style={{ color: data.primary_color }}>TikTok</a>}
        </footer>
      )}
    </div>
  );
}

// =========== Step 1 : Business ===========
function StepBusiness({ data, set, t = (m) => m, companyId }) {
  const INDUSTRIES = ["צילום וקולנוע", "יופי וטיפוח", "אופנה", "מסעדנות", "בריאות", "כושר", "ייעוץ עסקי", "טכנולוגיה", "חינוך", "אמנות ועיצוב", 'נדל"ן', "מסחר אונליין"];
  const [enhancing, setEnhancing] = useState(false);
  const [original, setOriginal] = useState(null); // allow "undo"
  const max = 1500;
  const used = (data.business_what || "").length;

  const enhanceWithAI = async () => {
    const text = (data.business_what || "").trim();
    if (text.length < 5) {
      toast.error("צריך לפחות כמה משפטים כדי שנוכל לשדרג");
      return;
    }
    setEnhancing(true);
    try {
      const { data: r } = await api.post(
        `/companies/${companyId}/brief/enhance-description`,
        { text, gender: data.gender || "" }
      );
      if (r.improved) {
        setOriginal(text);
        set("business_what", r.improved);
        toast.success("התיאור שודרג — לוחץ 'שחזר' כדי להחזיר את הגרסה המקורית");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שדרוג נכשל — נסה שוב");
    } finally { setEnhancing(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">{t("מה העסק שלך עושה? 💡", "מה העסק שלך עושה? 💡")}</h3>
        <p className="text-sm text-stone-500">{t(
          "ספר לנו בכמה משפטים את הליבה של העסק. כמה שיותר פירוט — טוב יותר.",
          "ספרי לנו בכמה משפטים את הליבה של העסק. כמה שיותר פירוט — טוב יותר.",
          "תאר לנו בכמה משפטים את הליבה של העסק. כמה שיותר פירוט — טוב יותר.",
        )}</p>
      </div>
      <label className="block">
        <span className="flex items-center justify-between text-xs font-bold text-stone-700">
          <span>תיאור מורחב * <span className="text-stone-400 font-normal">(עד {max} תווים — כמה שיותר עלייך, ייעזור לנו להבין)</span></span>
          <span className={`font-mono text-[10px] ${used > max ? "text-rose-500" : "text-stone-400"}`}>{used}/{max}</span>
        </span>
        <textarea value={data.business_what} onChange={(e) => set("business_what", e.target.value.slice(0, max))} rows={9} maxLength={max}
          placeholder={t(
            "לדוגמה: סטודיו לצילום בוטיק שמתמחה בחתונות ואירועים פרטיים. שירותינו כוללים אלבומים מודפסים, צילום מגנטים בזמן אמת ועריכה מהירה. הלקוחות שלנו מחפשים תוצרים מקצועיים שמשקפים את הסיפור האישי שלהם...",
            "לדוגמה: סטודיו לצילום בוטיק שמתמחה בחתונות ואירועים פרטיים. שירותינו כוללים אלבומים מודפסים, צילום מגנטים בזמן אמת ועריכה מהירה. הלקוחות שלנו מחפשים תוצרים מקצועיים שמשקפים את הסיפור האישי שלהן...",
          )}
          className="mt-1.5 w-full bg-white border-2 border-stone-200 focus:border-fuchsia-400 rounded-xl px-4 py-3 text-sm outline-none transition leading-relaxed"
          data-testid="brief-business-what" />
      </label>

      {/* AI-enhance button */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={enhanceWithAI}
          disabled={enhancing || used < 5}
          className="inline-flex items-center gap-1.5 bg-gradient-to-l from-fuchsia-500 via-violet-500 to-indigo-500 hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed text-white font-extrabold px-4 py-2.5 rounded-xl shadow-md text-sm transition-all"
          data-testid="brief-enhance-ai-btn"
        >
          {enhancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {enhancing ? "משדרג..." : "שדרג את התיאור עם AI"}
          <Sparkles className="h-3 w-3" />
        </button>
        {original && (
          <button
            type="button"
            onClick={() => { set("business_what", original); setOriginal(null); }}
            className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700 underline"
            data-testid="brief-enhance-undo-btn"
          >
            ↶ שחזר תיאור מקורי
          </button>
        )}
        <span className="text-[11px] text-stone-400 mr-auto">קצר, ברור וקריא — AI יעזור לעדן את הניסוח מבלי לאבד את המסר</span>
      </div>

      <div>
        <span className="text-xs font-bold text-stone-700">תחום עיסוק</span>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {INDUSTRIES.map((ind) => {
            const sel = data.business_industry === ind;
            return (
              <button key={ind} type="button" onClick={() => set("business_industry", sel ? "" : ind)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${sel ? "bg-gradient-to-l from-amber-400 to-rose-400 border-transparent text-white shadow-md" : "bg-white border-stone-200 text-stone-600 hover:border-amber-300"}`}
                data-testid={`brief-industry-${ind}`}>
                {ind}
              </button>
            );
          })}
        </div>
        <input type="text" value={INDUSTRIES.includes(data.business_industry) ? "" : data.business_industry}
          onChange={(e) => set("business_industry", e.target.value)} placeholder="או הקלידו תחום מותאם…"
          className="mt-2 w-full bg-stone-50 border border-stone-200 focus:border-amber-300 rounded-lg px-3 py-2 text-xs outline-none"
          data-testid="brief-industry-custom" />
      </div>
    </div>
  );
}

// =========== Step 2 : Design ===========
function StepDesign({ data, set, t = (m) => m }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">עיצוב האתר 🎨</h3>
        <p className="text-sm text-stone-500">3 צבעים, פונט וסוג כפתורים — תצוגה חיה משמאל.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <ColorField label="צבע ראשי" value={data.primary_color} onChange={(v) => set("primary_color", v)} testid="brief-color-primary" />
        <ColorField label="צבע משני" value={data.secondary_color} onChange={(v) => set("secondary_color", v)} testid="brief-color-secondary" />
        <ColorField label="צבע הדגשה" value={data.accent_color} onChange={(v) => set("accent_color", v)} testid="brief-color-accent" />
      </div>

      <div>
        <span className="text-xs font-bold text-stone-700">סוג פונט</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1.5">
          {FONT_OPTIONS.map((f) => {
            const sel = data.font_family === f.key;
            return (
              <button key={f.key} type="button" onClick={() => set("font_family", f.key)}
                className={`px-3 py-2.5 rounded-xl text-right text-sm border-2 transition ${sel ? "bg-gradient-to-l from-fuchsia-500 to-violet-500 border-transparent text-white shadow-md" : "bg-white border-stone-200 text-stone-700 hover:border-fuchsia-300"}`}
                style={{ fontFamily: f.css }}
                data-testid={`brief-font-${f.key}`}>
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-xs font-bold text-stone-700">סוג כפתורים</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5">
          {BUTTON_STYLES.map((b) => {
            const sel = data.button_style === b.key;
            return (
              <button key={b.key} type="button" onClick={() => set("button_style", b.key)}
                className={`p-3 border-2 transition flex flex-col items-center gap-1.5 ${sel ? "border-fuchsia-500 bg-fuchsia-50" : "border-stone-200 bg-white hover:border-fuchsia-300"} ${b.radius.split(" ")[0]}`}
                data-testid={`brief-button-${b.key}`}>
                <span className={`px-3 py-1 text-[10px] font-bold ${b.radius.split(" ")[0]} text-white`} style={{ background: data.primary_color, border: b.key === "outline" ? `2px solid ${data.primary_color}` : "none", color: b.key === "outline" ? data.primary_color : "white", backgroundColor: b.key === "outline" ? "white" : data.primary_color }}>
                  כפתור
                </span>
                <span className={`text-[10px] font-bold ${sel ? "text-fuchsia-700" : "text-stone-600"}`}>{b.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">{label}</span>
      <div className="mt-1 flex items-center gap-2 bg-white border-2 border-stone-200 rounded-xl p-2 focus-within:border-fuchsia-400 transition">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-9 rounded-md cursor-pointer border-0" data-testid={`${testid}-picker`} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-transparent outline-none text-sm font-mono text-stone-700 min-w-0" dir="ltr" data-testid={testid} />
      </div>
    </label>
  );
}

// =========== Step 3 : Details ===========
function StepDetails({ data, set, t = (m) => m }) {
  const TONES = ["ידידותי", "מקצועי", "יוקרתי", "צעיר ותוסס", "אישי וחם", "ישיר וחד"];
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">פירוט כללי על העסק ✨</h3>
        <p className="text-sm text-stone-500">תאר/י את העסק לעומק — קהל יעד, שירותים, שפה.</p>
      </div>
      <label className="block">
        <span className="text-xs font-bold text-stone-700">הסיפור של העסק</span>
        <textarea value={data.business_about} onChange={(e) => set("business_about", e.target.value)} rows={3}
          placeholder="ההיסטוריה, הייחוד, מה הופך אתכם לטובים…"
          className="mt-1.5 w-full bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl px-4 py-3 text-sm outline-none transition" data-testid="brief-about" />
      </label>
      <label className="block">
        <span className="text-xs font-bold text-stone-700">קהל היעד</span>
        <textarea value={data.target_audience} onChange={(e) => set("target_audience", e.target.value)} rows={2}
          placeholder="גילאים, מין, תחומי עניין…"
          className="mt-1.5 w-full bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl px-4 py-3 text-sm outline-none transition" data-testid="brief-audience" />
      </label>
      <div>
        <span className="text-xs font-bold text-stone-700">סגנון השפה</span>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {TONES.map((t) => {
            const sel = data.tone_of_voice === t;
            return (
              <button key={t} type="button" onClick={() => set("tone_of_voice", sel ? "" : t)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${sel ? "bg-gradient-to-l from-emerald-400 to-cyan-400 border-transparent text-white shadow-md" : "bg-white border-stone-200 text-stone-600 hover:border-emerald-300"}`}
                data-testid={`brief-tone-${t}`}>
                {t}
              </button>
            );
          })}
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-bold text-stone-700">שירותים / מוצרים מרכזיים</span>
        <textarea value={data.key_services} onChange={(e) => set("key_services", e.target.value)} rows={3}
          placeholder="פירוט של המוצרים/שירותים העיקריים…"
          className="mt-1.5 w-full bg-white border-2 border-stone-200 focus:border-emerald-400 rounded-xl px-4 py-3 text-sm outline-none transition" data-testid="brief-services" />
      </label>
    </div>
  );
}

// =========== Step 4 : Features & Socials ===========
function StepFeatures({ data, set, toggleFeature, t = (m) => m }) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">פיצ'רים ורשתות חברתיות 🚀</h3>
        <p className="text-sm text-stone-500">סמן את הפיצ'רים שתרצה ב-MVP, וקישורים לרשתות החברתיות.</p>
      </div>

      <div>
        <span className="text-xs font-bold text-stone-700 block mb-2">פיצ'רים שתרצה ב-MVP</span>
        <div className="grid sm:grid-cols-2 gap-2">
          {FEATURES.map((f) => {
            const sel = data.features.includes(f.key);
            return (
              <button key={f.key} type="button" onClick={() => toggleFeature(f.key)}
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-right transition ${sel ? "bg-gradient-to-l from-cyan-500 to-sky-500 border-transparent text-white shadow-md" : "bg-white border-stone-200 text-stone-700 hover:border-cyan-300"}`}
                data-testid={`brief-feature-${f.key}`}>
                <span className="text-xl flex-shrink-0">{f.emoji}</span>
                <span className="text-xs font-bold leading-tight">{f.label}</span>
                <span className={`mr-auto h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? "bg-white border-white" : "border-stone-300"}`}>
                  {sel && <Check className="h-3 w-3 text-cyan-500" strokeWidth={4} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-xs font-bold text-stone-700 block mb-2">רשתות חברתיות (אופציונלי)</span>
        <div className="space-y-2">
          {SOCIALS.map((s) => (
            <div key={s.key} className="flex items-center gap-2 bg-white border-2 border-stone-200 rounded-xl p-2 focus-within:border-cyan-400 transition">
              <span className="text-xs font-bold text-stone-600 w-20 flex-shrink-0">{s.label}</span>
              <input type="url" value={data[s.key] || ""} onChange={(e) => set(s.key, e.target.value)} placeholder={s.placeholder}
                className="flex-1 bg-transparent outline-none text-sm text-stone-800 min-w-0" dir="ltr" data-testid={`brief-social-${s.key}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =========== Step 5 : Files ===========
function StepFiles({ data, set, t = (m) => m }) {
  const inputRef = useRef(null);
  const handleFiles = (filesList) => {
    const arr = Array.from(filesList);
    Promise.all(arr.map((f) => new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve({ name: f.name, data: r.result, size: f.size, kind: f.type });
      r.readAsDataURL(f);
    }))).then((newFiles) => set("files", [...(data.files || []), ...newFiles]));
  };
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">קבצים לדוגמה 📁</h3>
        <p className="text-sm text-stone-500 leading-relaxed">
          {t(
            "לוגו, תמונות, מסמכים, פרסומים שאהבת — כל מה שיעזור לנו להבין. תוסיף כמה שיותר על העסק שלך, על הקול שלך, ועל מה שמשרת אותך טוב.",
            "לוגו, תמונות, מסמכים, פרסומים שאהבת — כל מה שיעזור לנו להבין. תוסיפי כמה שיותר על העסק שלך, על הקול שלך, ועל מה שמשרת אותך טוב.",
            "לוגו, תמונות, מסמכים — כל מה שיעזור לנו להבין. הוסף כמה שיותר עליך ועל העסק שלך.",
          )}
        </p>
      </div>
      <div onClick={() => inputRef.current?.click()} onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }} onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-sky-300 hover:border-sky-500 bg-gradient-to-br from-sky-50 to-indigo-50 rounded-2xl p-8 text-center cursor-pointer transition" data-testid="brief-files-dropzone">
        <div className="text-5xl mb-2">📤</div>
        <div className="font-bold text-stone-800 mb-1">גרור קבצים לכאן או לחץ להעלאה</div>
        <div className="text-xs text-stone-500">PNG, JPG, PDF, DOCX, MP4 — עד 20MB סה"כ</div>
        <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.mp4,.mov" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="brief-files-input" />
      </div>
      {(data.files || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="brief-files-grid">
          {(data.files || []).map((f, i) => (
            <div key={i} className="relative rounded-xl border-2 border-stone-200 bg-white overflow-hidden group">
              {f.kind?.startsWith("image/") ? <img src={f.data} alt={f.name} className="w-full h-24 object-cover" /> : <div className="w-full h-24 flex items-center justify-center bg-stone-100 text-4xl">📄</div>}
              <div className="px-2 py-1.5 text-[10px] font-bold truncate text-stone-700">{f.name}</div>
              <button type="button" onClick={() => set("files", data.files.filter((_, j) => j !== i))}
                className="absolute top-1 left-1 h-6 w-6 rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition inline-flex items-center justify-center" data-testid={`brief-file-remove-${i}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========== Step 6 : References ===========
function StepReferences({ data, set, t = (m) => m }) {
  const update = (i, field, val) => {
    const next = [...data.reference_urls]; next[i] = { ...next[i], [field]: val }; set("reference_urls", next);
  };
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900 mb-1">5 אתרים שאהבת 🌟</h3>
        <p className="text-sm text-stone-500">אתרים שמשמשים השראה לסגנון, פיצ'רים או תחושה.</p>
      </div>
      <div className="space-y-2.5">
        {data.reference_urls.map((r, i) => (
          <div key={i} className="rounded-2xl border-2 border-stone-200 bg-white p-3 hover:border-yellow-300 transition" data-testid={`brief-ref-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-yellow-300 to-lime-400 flex items-center justify-center text-white font-extrabold shrink-0">{i + 1}</div>
              <input type="url" value={r.url} onChange={(e) => update(i, "url", e.target.value)} placeholder="https://example.com"
                className="flex-1 bg-stone-50 border border-stone-200 focus:border-yellow-400 rounded-lg px-3 py-2 text-sm outline-none min-w-0" dir="ltr" data-testid={`brief-ref-url-${i}`} />
            </div>
            <input type="text" value={r.reason} onChange={(e) => update(i, "reason", e.target.value)} placeholder="מה אהבת באתר? (אופציונלי)"
              className="w-full bg-stone-50 border border-stone-200 focus:border-yellow-400 rounded-lg px-3 py-2 text-xs outline-none" data-testid={`brief-ref-reason-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =========== Post-Finish: Credits Pricing ===========
// =========== Step 7 (THE MOST IMPORTANT) : Automation pain points ===========
function StepAutomations({ data, set, t = (m) => m }) {
  const PAIN_CHIPS = [
    "לקוחות שלא חוזרים",
    "מענה אוטומטי לפניות",
    "ניהול תורים ופגישות",
    "תזכורות לתשלום",
    "שליחת חשבוניות",
    "ליווי לידים חדשים",
    "סקרי משוב אחרי שירות",
    "פוסטים אוטומטיים לרשתות",
    "ניהול מלאי",
    "חיבור CRM למייל/וואטסאפ",
    "דוחות חודשיים אוטומטיים",
    "סינון לידים לפי איכות",
    "ניתוב פניות לנציג הנכון",
    "יצירת תכנים עם AI",
  ];
  const togglePain = (chip) => {
    const cur = data.automation_pains || [];
    set("automation_pains", cur.includes(chip) ? cur.filter((x) => x !== chip) : [...cur, chip]);
  };
  const selected = data.automation_pains || [];
  const max = 2000;
  const used = (data.automation_needs || "").length;

  return (
    <div className="space-y-5">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-rose-500 via-fuchsia-500 to-violet-600 text-white shadow-lg">
        <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-amber-300/30 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-widest mb-2">
            🚀 השלב הכי חשוב
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold leading-tight mb-1">
            {t(
              "תספר לנו במה אתה נתקע מבחינת אוטומציה בעסק",
              "תספרי לנו במה את נתקעת מבחינת אוטומציה בעסק",
            )}
          </h3>
          <p className="text-sm text-white/90 leading-relaxed">
            {t(
              "מה היית רוצה שהעסק שלך יעשה לבד? כל מה שעולה בדעתך — תרשום כאן. אין אוטומציה שאנחנו לא יודעים לפתור 💪",
              "מה היית רוצה שהעסק שלך יעשה לבד? כל מה שעולה בדעתך — תרשמי כאן. אין אוטומציה שאנחנו לא יודעים לפתור 💪",
            )}
          </p>
        </div>
      </div>

      {/* Quick-add pain chips */}
      <div>
        <span className="text-xs font-extrabold text-stone-700 block mb-2">
          💡 רעיונות נפוצים — לחץ כדי להוסיף:
        </span>
        <div className="flex flex-wrap gap-1.5">
          {PAIN_CHIPS.map((chip) => {
            const isSel = selected.includes(chip);
            return (
              <button
                key={chip}
                type="button"
                onClick={() => togglePain(chip)}
                data-testid={`automation-pain-${chip}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                  isSel
                    ? "bg-gradient-to-l from-rose-500 to-fuchsia-500 border-transparent text-white shadow-md scale-[1.02]"
                    : "bg-white border-stone-200 text-stone-600 hover:border-fuchsia-400"
                }`}
              >
                {isSel ? "✓ " : "+ "}{chip}
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="text-[11px] text-fuchsia-600 font-bold mt-2">
            ✨ {selected.length} רעיונות נבחרו — הם יישלחו לצוות שלנו יחד עם התיאור החופשי
          </div>
        )}
      </div>

      {/* Free-form textarea */}
      <label className="block">
        <span className="flex items-center justify-between text-xs font-extrabold text-stone-700">
          <span>
            {t(
              "תאר בחופשיות — כל אוטומציה, רעיון או כאב שיש לך",
              "תארי בחופשיות — כל אוטומציה, רעיון או כאב שיש לך",
            )}
          </span>
          <span className={`font-mono text-[10px] ${used > max ? "text-rose-500" : "text-stone-400"}`}>{used}/{max}</span>
        </span>
        <textarea
          value={data.automation_needs}
          onChange={(e) => set("automation_needs", e.target.value.slice(0, max))}
          rows={9}
          maxLength={max}
          placeholder={t(
            "לדוגמה: אני רוצה שכל לקוח שמזמין יקבל אוטומטית תזכורת יום לפני, ואחרי השירות יקבל סקר משוב. בנוסף, לידים שמגיעים מהאתר ייפתחו אצלי ב-CRM ויקבלו מייל ראשוני תוך 60 שניות...",
            "לדוגמה: אני רוצה שכל לקוחה שמזמינה תקבל אוטומטית תזכורת יום לפני, ואחרי השירות תקבל סקר משוב. בנוסף, לידים שמגיעות מהאתר ייפתחו אצלי ב-CRM ויקבלו מייל ראשוני תוך 60 שניות...",
          )}
          className="mt-1.5 w-full bg-white border-2 border-rose-200 focus:border-fuchsia-500 rounded-xl px-4 py-3 text-sm outline-none transition leading-relaxed"
          data-testid="brief-automation-needs"
        />
      </label>

      {/* Closing promise */}
      <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 p-3 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-lg">💪</span>
        </div>
        <div className="text-xs text-stone-700 leading-relaxed">
          <strong className="text-emerald-700">אין אוטומציה שאנחנו לא יודעים לפתור.</strong> כל מה שתרשום כאן ייקרא על ידי הצוות, נחזור אליך עם תכנית עבודה — והאוטומציות שלך יוקמו בתוך ימים בודדים.
        </div>
      </div>
    </div>
  );
}



// ============== Thank-You modal — pops with confetti after brief is finished ==============
// ============== Intro popup — "מידע חשוב" at the very start ==============
function BriefIntroPopup({ onContinue, gender }) {
  const isFemale = gender === "female";
  return (
    <div className="fixed inset-0 z-[10006] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up" dir="rtl" data-testid="brief-intro-popup">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Festive header */}
        <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 px-6 py-7 text-center text-white overflow-hidden">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-yellow-300/30 blur-2xl" />
          <div className="absolute top-3 right-6 text-2xl animate-float">💡</div>
          <div className="absolute top-3 left-6 text-2xl animate-float" style={{ animationDelay: "0.5s" }}>⏱️</div>
          <div className="absolute bottom-3 right-10 text-2xl animate-float" style={{ animationDelay: "0.9s" }}>💰</div>
          <div className="relative">
            <div className="text-5xl mb-2">📢</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">מידע חשוב!</h2>
          </div>
        </div>
        {/* Body */}
        <div className="px-6 py-6 text-center space-y-4">
          <p className="text-stone-700 text-base leading-relaxed">
            כמה {isFemale ? "שתמלאי" : "שתמלא"} את האפיון עם <strong className="text-amber-600">פרטים מדויקים יותר</strong> —
            <br />
            ככה לאנשי הצוות יהיה קל יותר להקים עבורך את
            <strong className="text-fuchsia-700"> האוטומציה המדויקת ביותר </strong>
            עבור העסק שלך.
          </p>
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-gradient-to-l from-emerald-50 to-teal-50 border-2 border-emerald-300">
            <span className="text-2xl">✨</span>
            <div className="text-right">
              <div className="font-extrabold text-emerald-700 text-base">{isFemale ? "ובכך תחסכי" : "ובכך תחסוך"}</div>
              <div className="text-stone-700 text-sm font-bold">זמן וכסף!</div>
            </div>
          </div>
          <button
            onClick={onContinue}
            data-testid="brief-intro-continue"
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-l from-fuchsia-500 via-violet-500 to-indigo-500 text-white font-extrabold py-3.5 rounded-xl shadow-lg hover:opacity-90 transition text-base"
          >
            המשך
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}



// ============== Credit info modal — pops right before the top-up modal ==============
function CreditInfoModal({ gender, onContinue }) {
  const isFemale = gender === "female";
  return (
    <div className="fixed inset-0 z-[10005] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto animate-fade-up" dir="rtl" data-testid="credit-info-modal">
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden my-auto">
        {/* Festive header */}
        <div className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 px-6 py-7 text-center text-white overflow-hidden">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-amber-300/30 blur-2xl" />
          <div className="absolute top-3 right-6 text-2xl animate-float">⏸️</div>
          <div className="absolute top-3 left-6 text-2xl animate-float" style={{ animationDelay: "0.5s" }}>💡</div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur rounded-full px-3 py-0.5 text-[10px] font-extrabold uppercase tracking-widest mb-3">
              ⏳ רגע לפני סיום...
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mb-1">
              💎 מה זה קרדיט?
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 text-stone-700 text-[14px] leading-relaxed">
          <InfoRow icon="💳" color="from-emerald-100 to-emerald-50" border="border-emerald-300">
            <strong className="text-emerald-700">קרדיט = תשלום עבור פעולה.</strong> כל תכנות, קוד, חיבור מערכת, אוטומציה או שירות שאנחנו מבצעים עבורך — נספר בקרדיטים.
          </InfoRow>

          <InfoRow icon="🏦" color="from-sky-100 to-sky-50" border="border-sky-300">
            <strong className="text-sky-700">{isFemale ? "את רוכשת" : "אתה רוכש"} בנק קרדיטים</strong> — וככה משלמים <strong>רק על השירותים שאנחנו מבצעים בפועל</strong>. ללא הפתעות.
          </InfoRow>

          <InfoRow icon="⚡" color="from-fuchsia-100 to-pink-50" border="border-fuchsia-300">
            אנחנו עושים את מירב המאמצים לספק עבודה <strong className="text-fuchsia-700">מהירה, מקצועית ומדויקת</strong> — כדי לחסוך {isFemale ? "לך" : "לך"} כמה שיותר זמן וכסף.
          </InfoRow>

          <InfoRow icon="🚀" color="from-amber-100 to-orange-50" border="border-amber-300">
            {isFemale ? "את הולכת" : "אתה הולך"} לחוות תהליכים חדשים — לעבוד <strong>חכם יותר, יעיל יותר ומהיר יותר.</strong> פתרונות אוטומציה שיקחו את העסק שלך לשלב הבא ✨
          </InfoRow>

          <div className="rounded-2xl bg-gradient-to-l from-emerald-50 to-teal-50 border-2 border-emerald-300 p-4 text-center mt-2">
            <div className="font-heading font-extrabold text-lg text-emerald-700 mb-0.5">
              {isFemale ? "מוכנה" : "מוכן"} להתחיל? 🎯
            </div>
            <div className="text-xs text-stone-600">
              {isFemale ? "לחצי" : "לחץ"} על המשך 👇
            </div>
          </div>

          <button
            onClick={onContinue}
            data-testid="credit-info-continue"
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-l from-emerald-500 via-teal-500 to-cyan-500 text-white font-extrabold py-3.5 rounded-xl shadow-lg hover:opacity-90 hover:scale-[1.01] transition text-base"
          >
            המשך
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, color, border, children }) {
  return (
    <div className={`flex gap-3 items-start rounded-xl bg-gradient-to-l ${color} border-2 ${border} p-3`}>
      <div className="text-2xl flex-shrink-0 mt-0.5">{icon}</div>
      <div className="text-[13.5px] leading-relaxed">{children}</div>
    </div>
  );
}



function ThankYouModal({ onContinue }) {
  return (
    <div className="fixed inset-0 z-[10004] bg-black/70 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up" dir="rtl" data-testid="brief-thank-you-modal">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Festive gradient header */}
        <div className="relative bg-gradient-to-br from-fuchsia-500 via-violet-500 to-indigo-500 px-6 py-8 text-center text-white overflow-hidden">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-amber-300/30 blur-2xl" />
          <div className="absolute top-2 right-6 text-3xl animate-float">🎉</div>
          <div className="absolute top-2 left-6 text-3xl animate-float" style={{ animationDelay: "0.4s" }}>✨</div>
          <div className="absolute bottom-4 right-12 text-2xl animate-float" style={{ animationDelay: "0.8s" }}>🎊</div>
          <div className="absolute bottom-4 left-12 text-2xl animate-float" style={{ animationDelay: "1.2s" }}>🚀</div>
          <div className="relative">
            <div className="text-6xl mb-3">🙏</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mb-1">תודה מזמנך</h2>
            <p className="text-white/95 text-sm">כל פרט שנתת — חשוב!</p>
          </div>
        </div>
        {/* Body */}
        <div className="px-6 py-6 text-center space-y-3">
          <p className="text-stone-700 text-base leading-relaxed">
            <strong className="text-fuchsia-700">אנשי המקצוע הטובים ביותר</strong> יעברו על הפרטים <br />
            ונשוב אליך בהקדם האפשרי 💜
          </p>
          <button
            onClick={onContinue}
            data-testid="brief-thank-you-continue"
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-l from-emerald-500 via-teal-500 to-cyan-500 text-white font-extrabold py-3.5 rounded-xl shadow-lg hover:opacity-90 transition text-base"
          >
            המשך
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}



function CreditsPricingScreen({ company, onClose, onBuyCredits, onBackToBrief }) {
  const [selected, setSelected] = useState("standard");
  const PLANS = [
    {
      key: "starter",
      name: "Starter",
      tag: "מתחילים",
      price: 99,
      tone: "from-emerald-50 to-teal-50",
      ring: "ring-emerald-300",
      accent: "text-emerald-700",
      badge: "bg-emerald-100 text-emerald-700",
      btn: "from-emerald-500 to-teal-500",
      features: [
        "אוטומציה אחת בסיסית",
        "חיבור למערכת אחת (וואטסאפ / מייל)",
        "תמיכה בסיסית",
      ],
    },
    {
      key: "standard",
      name: "Standard",
      tag: "⭐ הכי נמכר",
      price: 299,
      featured: true,
      tone: "from-sky-50 to-cyan-50",
      ring: "ring-sky-400",
      accent: "text-sky-700",
      badge: "bg-sky-100 text-sky-700",
      btn: "from-sky-500 to-cyan-500",
      features: [
        "עד 3 אוטומציות פעילות",
        "חיבור עד 3 מערכות (CRM / WhatsApp / Forms)",
        "דוחות חודשיים",
        "תמיכה רגילה",
      ],
    },
    {
      key: "pro",
      name: "Pro",
      tag: "לעסקים שגדלים",
      price: 799,
      tone: "from-violet-50 to-fuchsia-50",
      ring: "ring-violet-400",
      accent: "text-violet-700",
      badge: "bg-violet-100 text-violet-700",
      btn: "from-violet-500 to-fuchsia-500",
      features: [
        "עד 10 אוטומציות",
        "חיבורים ללא הגבלה (כמעט)",
        "Dashboard מותאם",
        "תמיכה מועדפת + אופטימיזציה חודשית",
      ],
    },
    {
      key: "premium",
      name: "Premium",
      tag: "Enterprise",
      price: 1999,
      pricePlus: true,
      tone: "from-rose-50 to-amber-50",
      ring: "ring-rose-400",
      accent: "text-rose-700",
      badge: "bg-rose-100 text-rose-700",
      btn: "from-rose-500 to-amber-500",
      features: [
        "אוטומציות ללא הגבלה",
        "פיתוח מותאם אישית",
        "API / Webhooks / מערכות מורכבות",
        "מנהל לקוח אישי · SLA · VIP",
      ],
    },
  ];
  const currentName = PLANS.find((p) => p.key === selected)?.name || "";
  return (
    <div className="fixed inset-0 z-[10003] bg-black/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="credits-pricing-screen">
      <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl my-4 max-h-[96vh] flex flex-col" dir="rtl">
        <button onClick={onClose} className="absolute top-3 left-3 h-8 w-8 rounded-lg text-white/90 hover:bg-white/20 inline-flex items-center justify-center z-10" data-testid="pricing-close">
          <X className="h-4 w-4" />
        </button>
        {onBackToBrief && (
          <button
            onClick={onBackToBrief}
            className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-extrabold backdrop-blur transition"
            data-testid="pricing-back-to-brief"
            title="חזרה לעריכת האפיון"
          >
            <ChevronRight className="h-3.5 w-3.5" />
            חזור לערוך את האפיון
          </button>
        )}

        {/* Compact hero */}
        <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-fuchsia-600 via-violet-600 to-indigo-700 text-white px-6 py-4 text-center flex-shrink-0">
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex items-center justify-center gap-3">
            <div className="text-2xl">🎉</div>
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-extrabold">האפיון נשמר! בחר את החבילה שלך</h2>
              <p className="text-white/85 text-[11px]">4 מסלולים מתאימים לכל גודל עסק — אפשר לשדרג בכל עת.</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* 4-tier pricing grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {PLANS.map((p) => {
              const active = selected === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setSelected(p.key)}
                  className={`relative text-right rounded-2xl border-2 p-3 transition-all ${
                    active
                      ? `bg-gradient-to-br ${p.tone} ${p.ring} ring-2 border-transparent scale-[1.02] shadow-lg`
                      : "bg-white border-stone-200 hover:border-stone-400"
                  }`}
                  data-testid={`pricing-plan-${p.key}`}
                >
                  {p.featured && (
                    <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500 text-white text-[9px] font-extrabold shadow">⭐ הכי נמכר</span>
                  )}
                  <div className={`text-[10px] font-extrabold uppercase tracking-widest ${p.accent}`}>{p.name}</div>
                  <div className="text-[10px] text-stone-500 mb-2">{p.tag}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-2xl font-extrabold text-stone-900">₪{p.price}</span>
                    {p.pricePlus && <span className="text-xs font-bold text-stone-600">+</span>}
                    <span className="text-[10px] text-stone-500">/חודש</span>
                  </div>
                  <ul className="mt-2.5 space-y-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-1 text-[10.5px] leading-snug text-stone-700">
                        <Check className={`h-3 w-3 mt-0.5 flex-shrink-0 ${p.accent}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {active && (
                    <div className={`mt-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold ${p.badge}`}>
                      ✓ נבחר
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* One-time setup (paid via credits) */}
          <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">💎</span>
                <div>
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700">דמי הקמה חד-פעמיים</div>
                  <div className="font-heading text-base font-extrabold text-stone-900">משלמים בקרדיטים — לפי היקף ההקמה</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mr-auto text-[10.5px]">
                <span className="px-2 py-1 rounded-md bg-white border border-amber-200 font-bold text-stone-700">קצב המרה: <span className="text-emerald-700">1,000 קרדיטים = ₪600</span></span>
                <span className="px-2 py-1 rounded-md bg-white border border-amber-200 font-bold text-stone-700">הקמה ממוצעת: <span className="text-fuchsia-700">~2,000 קרדיטים</span></span>
              </div>
            </div>
          </div>

          {/* Fine print */}
          <ul className="rounded-xl bg-stone-50 border border-stone-200 p-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-stone-700">
            <Li>💎 כל אפליקציה נוספת — לפי מחירון ספציפי.</Li>
            <Li>🛠 תפעול ושינויים — לפי שעות פיתוח.</Li>
            <Li>📈 שקיפות מלאה — תמיד רואים יתרה והיסטוריה.</Li>
            <Li>👀 דוגמאות לאוטומציות שכבר בנינו — מטה.</Li>
          </ul>
        </div>

        {/* Sticky CTA bar */}
        <div className="flex-shrink-0 border-t border-stone-200 bg-white/95 backdrop-blur p-3 rounded-b-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2">
            <button
              onClick={onBuyCredits}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-l from-emerald-500 via-teal-500 to-cyan-500 text-white font-extrabold py-3 px-5 rounded-xl shadow-lg hover:opacity-90 transition text-sm"
              data-testid="pricing-buy-now"
            >
              💎 התחל עם {currentName} — רכוש קרדיטים
            </button>
            <a href={`tel:${REP_PHONE}`} className="inline-flex items-center justify-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white font-bold py-3 px-4 rounded-xl text-xs transition" data-testid="pricing-call">
              <Phone className="h-3.5 w-3.5" /> {REP_PHONE}
            </a>
            <a href={REP_WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-4 rounded-xl text-xs transition" data-testid="pricing-whatsapp">
              <MessageCircleMore className="h-3.5 w-3.5" /> וואטסאפ
            </a>
            <a href={EXAMPLES_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 bg-white border-2 border-stone-300 hover:border-fuchsia-400 text-stone-700 font-bold py-3 px-4 rounded-xl text-xs transition" data-testid="pricing-examples">
              <ExternalLink className="h-3.5 w-3.5" /> דוגמאות
            </a>
          </div>
          <div className="flex items-center gap-2 justify-center mt-2 text-[10px] text-stone-400">
            <CreditCard className="h-3 w-3" /> אשראי
            <span>·</span>
            <Smartphone className="h-3 w-3" /> ביט / PayBox
            <span>·</span>
            <Landmark className="h-3 w-3" /> העברה בנקאית
          </div>
        </div>
      </div>
    </div>
  );
}


function Li({ children }) {
  return <li className="flex items-start gap-1.5"><span className="text-stone-400 mt-0.5">▸</span><span>{children}</span></li>;
}
